// ============================================================================
// FlightAware API Layer
// ============================================================================
// Thin client over the `flightaware-sync` edge function. The edge function
// holds the AeroAPI key (platform secret) and does the matching/enrichment;
// the frontend just triggers it and renders the result.

import { supabase } from '@/lib/supabase'

export interface FlightAwareFlight {
  faFlightId: string
  ident: string | null
  registration: string | null
  aircraftType: string | null
  direction: 'inbound' | 'outbound'
  scheduledAt: string | null
  estimatedAt: string | null
  originAirport: string | null
  destinationAirport: string | null
  originName?: string | null
  originCity?: string | null
  destinationName?: string | null
  destinationCity?: string | null
  operator: string | null
  status: string | null
  atcIdent?: string | null
  route?: string | null
  filedEte?: number | null
  progressPercent?: number | null
  cancelled?: boolean | null
  diverted?: boolean | null
  importable: boolean
}

export interface FlightAwareMatch {
  scheduleId: string
  tail: string
  direction: 'inbound' | 'outbound'
  previousTime: string | null
  newTime: string | null
  status: string | null
  ident: string | null
}

export interface FlightAwareSyncResult {
  fetchedAt: string
  airportIcao: string
  horizonHours: number
  counts: { fetched: number; matched: number; newAtAirport: number }
  matched: FlightAwareMatch[]
  newAtAirport: FlightAwareFlight[]
}

async function callFlightAware<T>(body: Record<string, unknown>): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) throw new Error('Not authenticated')

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  const response = await fetch(`${supabaseUrl}/functions/v1/flightaware-sync`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(body),
  })

  const result = await response.json()
  if (!response.ok) {
    throw new Error(result.error ?? 'FlightAware request failed')
  }
  return result as T
}

export function syncFlightAware(
  organizationId: string,
  locationId: string,
  horizonHours = 12,
): Promise<FlightAwareSyncResult> {
  return callFlightAware<FlightAwareSyncResult>({
    action: 'sync',
    organization_id: organizationId,
    location_id: locationId,
    horizonHours,
  })
}

export function importFlightAwareFlight(
  organizationId: string,
  locationId: string,
  flight: FlightAwareFlight,
): Promise<{ schedule_id: string; alreadyImported?: boolean }> {
  return callFlightAware({
    action: 'import',
    organization_id: organizationId,
    location_id: locationId,
    flight,
  })
}

export function importFlightAwareFlightsBulk(
  organizationId: string,
  locationId: string,
  flights: FlightAwareFlight[],
): Promise<{ imported: number; alreadyImported: number; skipped: number; scheduleIds: string[] }> {
  return callFlightAware({
    action: 'import_bulk',
    organization_id: organizationId,
    location_id: locationId,
    flights,
  })
}

export function setFlightAwareEnabled(
  organizationId: string,
  locationId: string,
  enabled: boolean,
): Promise<{ enabled: boolean; credential_id?: string }> {
  return callFlightAware({
    action: enabled ? 'enable_location' : 'disable_location',
    organization_id: organizationId,
    location_id: locationId,
  })
}
